import { useState, useMemo } from "react";
import { useExpenses, type Expense, type ExpenseFilters as BackendFilters } from "@/hooks/useExpenses";
import { useProperties } from "@/hooks/useProperties";
import { useExpenseTags } from "@/hooks/useExpenseTags";
import { useExpenseCategories } from "@/hooks/data/useEntityCategories";
import useRolePermissions from "@/hooks/admin/shared/useRolePermissions";
import { useDebounce } from "@/hooks/useDebounce";

export const useExpenseFilters = () => {
  const { role } = useRolePermissions();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();

  // Debounced search term
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Backend filters
  const backendFilters = useMemo<BackendFilters>(() => ({
    propertyIds: selectedProperties.length > 0 ? selectedProperties : undefined,
    categoryId: selectedCategory || undefined,
    dateFrom: dateFrom?.toISOString().split('T')[0],
    dateTo: dateTo?.toISOString().split('T')[0],
    tagId: selectedTag || undefined,
    organizationIds: selectedOrganizations.length > 0 ? selectedOrganizations : undefined,
    searchTerm: debouncedSearchTerm.trim() || undefined,
  }), [selectedProperties, selectedCategory, dateFrom, dateTo, selectedTag, selectedOrganizations, debouncedSearchTerm]);

  const { data: paginatedData, isLoading, error, refetch } = useExpenses(
    backendFilters,
    { page: currentPage, pageSize, orderBy: 'expense_date', orderDirection: 'desc' }
  );
  
  const { data: properties = [] } = useProperties();
  const { data: tags = [] } = useExpenseTags();
  const { data: categories = [] } = useExpenseCategories();

  // Backend handles all filtering now
  const filteredExpenses = paginatedData?.data || [];

  const hasActiveFilters = !!(
    searchTerm.trim() ||
    selectedCategory ||
    selectedTag ||
    selectedOrganizations.length > 0 ||
    selectedProperties.length > 0 ||
    dateFrom ||
    dateTo
  );

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategory("");
    setSelectedTag("");
    setSelectedOrganizations([]);
    setSelectedProperties([]);
    setDateFrom(undefined);
    setDateTo(undefined);
    setCurrentPage(1);
  };

  // Pagination handlers
  const goToPage = (page: number) => setCurrentPage(page);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, paginatedData?.totalPages || 1));
  const prevPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
  const changePageSize = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Reset to page 1 when filters change
  const handleSetSearchTerm = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleSetSelectedCategory = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handleSetSelectedTag = (tag: string) => {
    setSelectedTag(tag);
    setCurrentPage(1);
  };

  const handleSetSelectedOrganizations = (organizations: string[]) => {
    setSelectedOrganizations(organizations);
    setCurrentPage(1);
  };

  const handleSetSelectedProperties = (properties: string[]) => {
    setSelectedProperties(properties);
    setCurrentPage(1);
  };

  const handleSetDateFrom = (date: Date | undefined) => {
    setDateFrom(date);
    setCurrentPage(1);
  };

  const handleSetDateTo = (date: Date | undefined) => {
    setDateTo(date);
    setCurrentPage(1);
  };

  return {
    // Data
    filteredExpenses,
    categories,
    properties,
    tags,
    
    // Loading states
    isLoading,
    error,
    refetch,
    
    // Pagination
    currentPage,
    pageSize,
    totalCount: paginatedData?.totalCount || 0,
    totalPages: paginatedData?.totalPages || 1,
    goToPage,
    nextPage,
    prevPage,
    changePageSize,
    
    // Filter states
    searchTerm,
    selectedCategory,
    selectedTag,
    selectedOrganizations,
    selectedProperties,
    dateFrom,
    dateTo,
    hasActiveFilters,
    
    // Filter setters
    setSearchTerm: handleSetSearchTerm,
    setSelectedCategory: handleSetSelectedCategory,
    setSelectedTag: handleSetSelectedTag,
    setSelectedOrganizations: handleSetSelectedOrganizations,
    setSelectedProperties: handleSetSelectedProperties,
    setDateFrom: handleSetDateFrom,
    setDateTo: handleSetDateTo,
    clearFilters,
  };
};